import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Menu, Settings2 } from 'lucide-react';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import { format } from 'date-fns';

import {
  ApiAutocompleteResponse,
  ApiSearchSelectProps,
  DateRangeOption,
  SearchInputRenderParams,
  SelectSearchOption
} from './type';
import { formatMultipleSearchValue, parseMultipleSearchValue } from './utils';
import { DateRangeFilterDialog } from './date-range-filter-dialog';
import { ErpPopover } from '@/components/custom/erp/popover';
import { getDefaultDateOptions } from './constants';
import { BaseSearchTrigger } from './base-trigger';
import { useAuth } from '@/contexts/auth-context';
import { Checkbox } from '@/components/ui';
import api from '@/lib/api';

// --- Multiple Search Select -----------------------------------------

export interface MultipleSearchSelectProps extends Omit<SearchInputRenderParams, 'onKeyDown'> {
  options: Array<SelectSearchOption>;
  onCustomFilter?: () => void;
  placeholder?: string;
}

export const MultipleSearchSelect: React.FC<MultipleSearchSelectProps> = ({
  value,
  onChange,
  onBlur,
  options = [],
  placeholder = ''
}) => {
  const [open, setOpen] = useState(false);
  const [tempSelectedValues, setTempSelectedValues] = useState<string[]>([]);
  const triggerRef = useRef<HTMLInputElement>(null);

  const selectedValues = useMemo(() => parseMultipleSearchValue(value), [value]);

  useEffect(() => {
    if (open) setTempSelectedValues(selectedValues);
  }, [open, selectedValues]);

  const getDisplayText = () => selectedValues.join(';');
  const displayValue = getDisplayText();

  const [inputValue, setInputValue] = useState(displayValue);
  const [isUserTyping, setIsUserTyping] = useState(false);

  useEffect(() => {
    if (!open) {
      setInputValue(displayValue);
      setIsUserTyping(false);
    }
  }, [displayValue, open]);

  const filteredOptions =
    isUserTyping && inputValue
      ? options.filter(
          opt =>
            opt.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            opt.value.toLowerCase().includes(inputValue.toLowerCase())
        )
      : options;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsUserTyping(true);
    if (!open) setOpen(true);
  };

  const handleInputClick = () => setOpen(true);

  const syncValueFromInputAndApply = () => {
    const parsedValues = inputValue
      .split(';')
      .map(v => v.trim())
      .filter(Boolean);
    const validValues = parsedValues.filter(v => options.some(opt => opt.value === v));

    if (validValues.join(';') !== displayValue) {
      onChange(formatMultipleSearchValue(validValues));
    } else if (!inputValue.trim() && displayValue) {
      onChange('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!inputValue.trim()) {
        handleClearFilter();
      } else {
        syncValueFromInputAndApply();
        setOpen(false);
      }
    }
  };

  const handleToggleOption = (optionValue: string) => {
    setTempSelectedValues(prev =>
      prev.includes(optionValue) ? prev.filter(v => v !== optionValue) : [...prev, optionValue]
    );
  };

  const handleApplyFilter = () => {
    onChange(formatMultipleSearchValue(tempSelectedValues));
    setOpen(false);
  };

  const handleClearFilter = () => {
    onChange('');
    setInputValue('');
    setTempSelectedValues([]);
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      if (inputValue.trim() !== displayValue) {
        syncValueFromInputAndApply();
      } else {
        setInputValue(displayValue);
      }
      setTempSelectedValues(selectedValues);
      onBlur();
    }
  };

  return (
    <ErpPopover
      open={open}
      onOpenChange={handleOpenChange}
      disableToggle={true}
      onInteractOutside={(e: any) => {
        const triggerDiv = (e.target as Element)?.closest?.('.base-search-trigger');
        if (triggerDiv && triggerRef.current && triggerDiv.contains(triggerRef.current)) {
          e.preventDefault();
        }
      }}
      trigger={
        <BaseSearchTrigger
          ref={triggerRef}
          value={inputValue}
          hasValue={!!value}
          isOpen={open}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
      }
      content={
        <div className='flex max-h-[400px] min-w-full flex-col'>
          <div className='max-h-[320px] overflow-y-auto'>
            {filteredOptions.length > 0 ? (
              filteredOptions.map(opt => (
                <div
                  key={opt.value}
                  className='flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-100'
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => handleToggleOption(opt.value)}
                >
                  <Checkbox checked={tempSelectedValues.includes(opt.value)} />
                  <span>{opt.label}</span>
                </div>
              ))
            ) : (
              <div className='px-3 py-2 text-xs text-gray-500'>Không tìm thấy kết quả</div>
            )}
          </div>
          <div className='flex items-center justify-between border-t px-2 py-2'>
            <button
              onMouseDown={e => e.preventDefault()}
              onClick={handleApplyFilter}
              className='flex items-center gap-1 rounded bg-main px-2 py-1.5 text-xs font-semibold text-main-foreground hover:bg-main-dark'
              type='button'
            >
              <Settings2 className='size-3.5' />
              Lọc dữ liệu
            </button>
            <button
              onMouseDown={e => e.preventDefault()}
              onClick={handleClearFilter}
              className='flex items-center text-gray-600 hover:text-gray-800'
              type='button'
            >
              <Lineicons aria-hidden='true' icon={XmarkStroke} />
            </button>
          </div>
        </div>
      }
      contentClassName='w-48 p-0'
    />
  );
};

// --- Date Search Select ---------------------------------------------

export interface DateSearchSelectProps extends Omit<SearchInputRenderParams, 'onKeyDown'> {
  options?: DateRangeOption[];
  onCustomFilter?: () => void;
  placeholder?: string;
}

export const DateSearchSelect: React.FC<DateSearchSelectProps> = ({
  value,
  onChange,
  onBlur,
  options,
  onCustomFilter,
  placeholder = ''
}) => {
  const [open, setOpen] = useState(false);
  const [customFilterOpen, setCustomFilterOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const triggerRef = useRef<HTMLInputElement>(null);

  const dateOptions = useMemo(() => options || getDefaultDateOptions(), [options]);

  const displayValue = value || '';

  useEffect(() => {
    if (!open) setInputValue(displayValue);
  }, [displayValue, open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (!open) setOpen(true);
  };

  const handleInputClick = () => setOpen(true);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!inputValue.trim()) {
        handleClearFilter();
      } else {
        onChange(inputValue.trim());
        setOpen(false);
      }
    }
  };

  const handleSelectOption = (option: DateRangeOption) => {
    const { from, to } = option.getDateRange();
    const newValue = `>=${format(from, 'dd/MM/yyyy')}&<=${format(to, 'dd/MM/yyyy')}`;
    onChange(newValue);
    setInputValue(newValue);
    setOpen(false);
  };

  const handleCustomFilter = () => {
    setOpen(false);
    setCustomFilterOpen(true);
    onCustomFilter?.();
  };

  const handleApplyCustomFilter = (nextValue: string) => {
    setInputValue(nextValue);
    onChange(nextValue);
    setCustomFilterOpen(false);
  };

  const handleClearFilter = () => {
    onChange('');
    setInputValue('');
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      if (inputValue.trim() && inputValue.trim() !== displayValue) {
        onChange(inputValue.trim());
      } else if (!inputValue.trim() && displayValue) {
        onChange('');
      } else {
        setInputValue(displayValue);
      }
      onBlur();
    }
  };

  return (
    <>
      <ErpPopover
        open={open}
        onOpenChange={handleOpenChange}
        disableToggle={true}
        onInteractOutside={(e: any) => {
          const triggerDiv = (e.target as Element)?.closest?.('.base-search-trigger');
          if (triggerDiv && triggerRef.current && triggerDiv.contains(triggerRef.current)) {
            e.preventDefault();
          }
        }}
        trigger={
          <BaseSearchTrigger
            ref={triggerRef}
            value={inputValue}
            hasValue={!!value}
            isOpen={open}
            onChange={handleInputChange}
            onClick={handleInputClick}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
          />
        }
        content={
          <div className='flex max-h-[400px] min-w-full flex-col'>
            <div className='max-h-[320px] overflow-y-auto'>
              {dateOptions.length > 0 ? (
                dateOptions.map(opt => (
                  <div
                    key={opt.value}
                    className='cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-100'
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => handleSelectOption(opt)}
                  >
                    {opt.label}
                  </div>
                ))
              ) : (
                <div className='px-3 py-2 text-xs text-gray-500'>Không tìm thấy kết quả</div>
              )}
            </div>
            <div className='flex items-center justify-between border-t px-2 py-2'>
              <button
                onMouseDown={e => e.preventDefault()}
                onClick={handleCustomFilter}
                className='flex items-center gap-1 rounded bg-main px-2 py-1.5 text-xs font-semibold text-main-foreground hover:bg-main-dark'
                type='button'
              >
                <Settings2 className='size-3.5' />
                Lọc khác
              </button>
              <button
                onMouseDown={e => e.preventDefault()}
                onClick={handleClearFilter}
                className='flex items-center text-gray-600 hover:text-gray-800'
                type='button'
              >
                <Lineicons aria-hidden='true' icon={XmarkStroke} />
              </button>
            </div>
          </div>
        }
        contentClassName='w-48 p-0'
      />
      <DateRangeFilterDialog
        open={customFilterOpen}
        value={displayValue}
        onClose={() => setCustomFilterOpen(false)}
        onApply={handleApplyCustomFilter}
      />
    </>
  );
};

// --- Api Search Select ----------------------------------------------

export const ApiSearchSelect: React.FC<ApiSearchSelectProps> = ({
  value,
  onChange,
  onBlur,
  apiEndpoint,
  model,
  columns,
  valueKey = 'uuid',
  placeholder = '',
  searchField
}) => {
  const { entity } = useAuth();
  const [open, setOpen] = useState(false);
  const [tempSelectedValues, setTempSelectedValues] = useState<string[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [prevUrl, setPrevUrl] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const selectedValues = useMemo(() => parseMultipleSearchValue(value), [value]);

  const getDisplayText = useCallback(() => selectedValues.join(';'), [selectedValues]);
  const displayValue = getDisplayText();
  const [inputValue, setInputValue] = useState(displayValue);

  useEffect(() => {
    if (open) setTempSelectedValues(selectedValues);
  }, [open, selectedValues]);

  useEffect(() => {
    if (!open) setInputValue(displayValue);
  }, [displayValue, open]);

  const fetchData = useCallback(
    async (search: string, url?: string) => {
      setLoading(true);
      try {
        let requestUrl = url;
        if (!requestUrl) {
          const params = new URLSearchParams();
          params.set('model', model);
          const searchValue = searchField ? (search ? `${searchField},${search}` : searchField) : search;
          if (searchValue) params.set('search', searchValue);
          const baseUrl = `/entities/${entity?.slug}/erp/${apiEndpoint}/`;
          requestUrl = `${baseUrl}?${params.toString()}`;
        }
        const response = await api.get<ApiAutocompleteResponse>(requestUrl);
        setResults(response.data.results || []);
        setNextUrl(response.data.next);
        setPrevUrl(response.data.previous);
      } catch (error) {
        console.error('Failed to fetch autocomplete data:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [apiEndpoint, model, searchField, entity?.slug]
  );

  useEffect(() => {
    if (open) fetchData(searchTerm);
    const debounceTimer = debounceTimerRef.current;

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [open, searchTerm, fetchData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    setSearchTerm(val);
    if (!open) setOpen(true);
  };

  const handleInputClick = () => setOpen(true);

  const syncValueFromInputAndApply = () => {
    const parsedValues = inputValue
      .split(';')
      .map(v => v.trim())
      .filter(Boolean);
    onChange(formatMultipleSearchValue(parsedValues));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!inputValue.trim()) {
        handleClearFilter();
      } else if (tempSelectedValues.length > 0) {
        handleApplyFilter();
      } else {
        syncValueFromInputAndApply();
        setOpen(false);
      }
    }
  };

  const handleToggleOption = (optionValue: string) => {
    setTempSelectedValues(prev =>
      prev.includes(optionValue) ? prev.filter(v => v !== optionValue) : [...prev, optionValue]
    );
  };

  const handleApplyFilter = () => {
    onChange(formatMultipleSearchValue(tempSelectedValues));
    setOpen(false);
  };

  const handleClearFilter = () => {
    onChange('');
    setInputValue('');
    setSearchTerm('');
    setTempSelectedValues([]);
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      if (!inputValue.trim()) {
        if (displayValue) onChange('');
      } else if (inputValue.trim() !== displayValue) {
        syncValueFromInputAndApply();
      } else {
        setInputValue(displayValue);
      }
      setSearchTerm('');
      setTempSelectedValues(selectedValues);
      onBlur();
    }
  };

  return (
    <ErpPopover
      open={open}
      onOpenChange={handleOpenChange}
      disableToggle={true}
      onInteractOutside={(e: any) => {
        const triggerDiv = (e.target as Element)?.closest?.('.base-search-trigger');
        if (triggerDiv && triggerRef.current && triggerDiv.contains(triggerRef.current)) {
          e.preventDefault();
        }
      }}
      trigger={
        <BaseSearchTrigger
          ref={triggerRef}
          value={inputValue}
          hasValue={!!value}
          isOpen={open}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
      }
      content={
        <div className='flex max-h-[450px] min-w-full flex-col' ref={containerRef}>
          <div className='max-h-[320px] overflow-y-auto'>
            {loading ? (
              <div className='px-3 py-4 text-center text-xs text-gray-500'>Đang tải...</div>
            ) : results.length > 0 ? (
              results.map(item => {
                const itemValue = item[valueKey];
                const isSelected = tempSelectedValues.includes(itemValue);
                return (
                  <div
                    key={itemValue}
                    className='flex cursor-pointer items-center border-b border-gray-100 text-xs hover:bg-gray-50'
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => handleToggleOption(itemValue)}
                  >
                    <div className='w-8 flex-shrink-0 px-2 py-1.5'>
                      <Checkbox checked={isSelected} />
                    </div>
                    {columns.map(col => (
                      <div
                        key={col.key}
                        className='flex-1 truncate px-2 py-1.5'
                        style={{ width: col.width, minWidth: col.width }}
                        title={item[col.key] || ''}
                      >
                        {item[col.key] || ''}
                      </div>
                    ))}
                  </div>
                );
              })
            ) : (
              <div className='px-3 py-4 text-center text-xs text-gray-500'>Không tìm thấy kết quả</div>
            )}
          </div>
          <div className='flex items-center justify-between border-t px-2 py-2'>
            <button
              onMouseDown={e => e.preventDefault()}
              onClick={handleApplyFilter}
              className='flex items-center gap-1 rounded bg-main px-3 py-1.5 text-xs font-semibold text-main-foreground hover:bg-main-dark'
              type='button'
            >
              <Settings2 className='size-3.5' />
              Lọc dữ liệu
            </button>
            <div className='flex items-center gap-1'>
              <button
                onMouseDown={e => e.preventDefault()}
                onClick={handleClearFilter}
                className='flex h-7 w-7 items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100'
                type='button'
                title='Xóa bộ lọc'
              >
                <Menu className='size-4' />
              </button>
              <button
                onMouseDown={e => e.preventDefault()}
                onClick={() => prevUrl && fetchData(searchTerm, prevUrl)}
                disabled={!prevUrl}
                className='flex h-7 w-7 items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50'
                type='button'
              >
                <ChevronLeft className='size-4' />
              </button>
              <button
                onMouseDown={e => e.preventDefault()}
                onClick={() => nextUrl && fetchData(searchTerm, nextUrl)}
                disabled={!nextUrl}
                className='flex h-7 w-7 items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50'
                type='button'
              >
                <ChevronRight className='size-4' />
              </button>
            </div>
          </div>
        </div>
      }
      contentClassName='w-[500px] p-0'
    />
  );
};
